import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  XMarkIcon, 
  SparklesIcon, 
  RocketLaunchIcon, 
  StarIcon, 
  ShieldCheckIcon, 
  BellIcon,
  PlusIcon,
  TrashIcon,
  PhotoIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { featureAnnouncementsAPI, usersAPI } from '../services/api';
import axios from 'axios';
import { toast } from 'react-toastify';

const iconOptions = [
  { id: 'sparkles', name: 'Sparkles', icon: SparklesIcon },
  { id: 'rocket', name: 'Rocket', icon: RocketLaunchIcon },
  { id: 'star', name: 'Star', icon: StarIcon },
  { id: 'shield', name: 'Shield', icon: ShieldCheckIcon },
  { id: 'bell', name: 'Bell', icon: BellIcon },
  { id: 'zap', name: 'Lightning', icon: SparklesIcon }, // Fallback icon
];

function FeatureAnnouncementForm({ isOpen, onClose, announcement, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploadingIllustration, setUploadingIllustration] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    featureIcon: 'sparkles',
    featureIllustration: '',
    benefits: [''],
    targetType: 'all',
    targetIds: [],
    displayAsModal: true,
    modalPriority: 0,
    expiresAt: '',
    priority: 'normal'
  });

  useEffect(() => {
    if (isOpen) {
      loadDepartments();
      loadUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (announcement) {
      setFormData({
        title: announcement.title || '',
        content: announcement.content || '',
        featureIcon: announcement.featureIcon || 'sparkles',
        featureIllustration: announcement.featureIllustration || '',
        benefits: announcement.benefits && announcement.benefits.length > 0 ? announcement.benefits : [''],
        targetType: announcement.targetType || 'all',
        targetIds: announcement.targetIds || [],
        displayAsModal: announcement.displayAsModal ?? true,
        modalPriority: announcement.modalPriority || 0,
        expiresAt: announcement.expiresAt ? new Date(announcement.expiresAt).toISOString().split('T')[0] : '',
        priority: announcement.priority || 'normal'
      });
    } else {
      setFormData({
        title: '',
        content: '',
        featureIcon: 'sparkles',
        featureIllustration: '',
        benefits: [''],
        targetType: 'all',
        targetIds: [],
        displayAsModal: true,
        modalPriority: 0,
        expiresAt: '',
        priority: 'normal'
      });
    }
  }, [announcement, isOpen]);

  const loadDepartments = async () => {
    try {
      const response = await usersAPI.getDepartmentsWithCount();
      if (response.data && response.data.departments) {
        setDepartments(response.data.departments);
      }
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getUsers({ limit: 1000, status: 'Active' });
      if (response.data && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleIllustrationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingIllustration(true);
    try {
      const token = localStorage.getItem('token');
      const presign = await axios.post('/api/uploads/presign', {
        fileName: file.name,
        contentType: file.type || 'image/jpeg',
        folder: 'feature-announcements'
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { uploadUrl, publicUrl, requiredHeaders } = presign.data;

      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          ...(requiredHeaders || {})
        },
        withCredentials: false
      });

      setFormData(prev => ({
        ...prev,
        featureIllustration: publicUrl
      }));
      toast.success('Illustration uploaded successfully');
    } catch (err) {
      console.error('Error uploading illustration:', err);
      // Fallback to server-side upload
      try {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('file', file);
        form.append('folder', 'feature-announcements');
        const fallback = await axios.post('/api/uploads/direct', form, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        if (fallback.data?.publicUrl) {
          setFormData(prev => ({
            ...prev,
            featureIllustration: fallback.data.publicUrl
          }));
          toast.success('Illustration uploaded successfully');
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback upload failed:', fallbackError);
      }
      toast.error('Failed to upload illustration');
    } finally {
      setUploadingIllustration(false);
    }
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const addBenefit = () => {
    setFormData({ ...formData, benefits: [...formData.benefits, ''] });
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: newBenefits.length > 0 ? newBenefits : [''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Filter out empty benefits
      const cleanedBenefits = formData.benefits.filter(b => b.trim() !== '');
      const payload = { ...formData, benefits: cleanedBenefits };

      let response;
      if (announcement) {
        response = await featureAnnouncementsAPI.update(announcement.id, payload);
      } else {
        response = await featureAnnouncementsAPI.create(payload);
      }

      if (response.data.success) {
        toast.success(`Feature announcement ${announcement ? 'updated' : 'created'} successfully!`);
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[110]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-3xl border border-slate-200">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-6 flex items-center justify-between">
                  <div>
                    <Dialog.Title as="h3" className="text-xl font-bold text-white">
                      {announcement ? '✏️ Edit Feature' : '✨ Create New Feature'}
                    </Dialog.Title>
                    <p className="text-indigo-100 text-xs mt-1">Design a premium announcement modal for users.</p>
                  </div>
                  <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 bg-white space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Feature Name (Title) *</label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                          placeholder="e.g., Advanced Analytics"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Description (Exciting summary) *</label>
                        <textarea
                          required
                          rows={3}
                          value={formData.content}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all resize-none"
                          placeholder="Explain what's new in 2-3 exciting sentences..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Feature Icon</label>
                        <div className="grid grid-cols-6 gap-2">
                          {iconOptions.map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, featureIcon: opt.id })}
                              className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center ${formData.featureIcon === opt.id ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
                            >
                              <opt.icon className="w-5 h-5" />
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Illustration (Upload or URL)</label>
                        <div className="space-y-3">
                          {formData.featureIllustration && (
                            <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-50 group">
                              <img 
                                src={formData.featureIllustration} 
                                alt="Illustration Preview" 
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, featureIllustration: '' })}
                                className="absolute top-2 right-2 p-1.5 bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <PhotoIcon className="h-5 w-5 text-slate-400" />
                              </div>
                              <input
                                type="text"
                                value={formData.featureIllustration}
                                onChange={(e) => setFormData({ ...formData, featureIllustration: e.target.value })}
                                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-sm"
                                placeholder="Paste image URL..."
                              />
                            </div>
                            
                            <label className={`flex items-center justify-center px-4 py-2 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all ${uploadingIllustration ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <DocumentArrowUpIcon className={`h-5 w-5 ${uploadingIllustration ? 'animate-bounce text-indigo-600' : 'text-slate-400'}`} />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleIllustrationUpload}
                                className="hidden"
                                disabled={uploadingIllustration}
                              />
                            </label>
                          </div>
                          {uploadingIllustration && (
                            <p className="text-[10px] text-indigo-600 font-medium animate-pulse">Uploading illustration...</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Benefits and Targeting */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-bold text-slate-700">Key Benefits</label>
                          <button
                            type="button"
                            onClick={addBenefit}
                            className="text-xs text-indigo-600 font-bold hover:underline flex items-center"
                          >
                            <PlusIcon className="w-3 h-3 mr-0.5" /> Add Benefit
                          </button>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                          {formData.benefits.map((benefit, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={benefit}
                                onChange={(e) => handleBenefitChange(index, e.target.value)}
                                className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 outline-none"
                                placeholder={`Benefit #${index + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => removeBenefit(index)}
                                className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Target Audience</label>
                          <select
                            value={formData.targetType}
                            onChange={(e) => setFormData({ ...formData, targetType: e.target.value, targetIds: [] })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value="all">Everyone</option>
                            <option value="all_managers">All Managers</option>
                            <option value="department">Specific Departments</option>
                            <option value="individual">Specific Users</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Priority Order</label>
                          <input
                            type="number"
                            value={formData.modalPriority}
                            onChange={(e) => setFormData({ ...formData, modalPriority: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      {formData.targetType === 'department' && (
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Select Departments</label>
                          <div className="border border-slate-200 rounded-xl p-3 max-h-32 overflow-y-auto grid grid-cols-2 gap-2">
                            {departments.map(dept => (
                              <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.targetIds.includes(dept.id)}
                                  onChange={(e) => {
                                    const newIds = e.target.checked
                                      ? [...formData.targetIds, dept.id]
                                      : formData.targetIds.filter(id => id !== dept.id);
                                    setFormData({ ...formData, targetIds: newIds });
                                  }}
                                  className="rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-slate-600 truncate">{dept.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {formData.targetType === 'individual' && (
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Select Users</label>
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              placeholder="Search users..."
                              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <div className="border border-slate-200 rounded-xl p-3 max-h-32 overflow-y-auto space-y-2">
                              {users
                                .filter(u => 
                                  `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.toLowerCase()) || 
                                  u.officialEmail.toLowerCase().includes(userSearch.toLowerCase())
                                )
                                .map(u => (
                                  <label key={u.id} className="flex items-center gap-2 cursor-pointer pb-1 border-b border-slate-50 last:border-0">
                                    <input
                                      type="checkbox"
                                      checked={formData.targetIds.includes(u.id)}
                                      onChange={(e) => {
                                        const newIds = e.target.checked
                                          ? [...formData.targetIds, u.id]
                                          : formData.targetIds.filter(id => id !== u.id);
                                        setFormData({ ...formData, targetIds: newIds });
                                      }}
                                      className="rounded text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-[10px] font-bold text-slate-700 truncate">{u.firstName} {u.lastName}</span>
                                      <span className="text-[9px] text-slate-400 truncate">{u.officialEmail}</span>
                                    </div>
                                  </label>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="displayAsModal"
                            checked={formData.displayAsModal}
                            onChange={(e) => setFormData({ ...formData, displayAsModal: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                          />
                          <label htmlFor="displayAsModal" className="text-sm font-bold text-slate-700">Enabled</label>
                        </div>
                        <div className="flex-1 text-[10px] text-slate-500 leading-tight">
                          When enabled, this feature will be eligible to show as a premium modal to targeted users.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2.5 rounded-xl border border-slate-300 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-8 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-100 disabled:opacity-50 transition-all"
                    >
                      {loading ? 'Saving...' : announcement ? 'Update Feature' : 'Create Feature'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default FeatureAnnouncementForm;
